<template>
  <Listbox as="div" :class="[classes]" class="!border-none bg-light-box-background dark:bg-dark-box-background p-0">
    <ListboxLabel
      v-if="label"
      class="block text-base font-medium text-gray-700 dark:text-dark-body-text bg-light-box-background dark:bg-dark-box-background"
      :for="name">
      {{ label }}
    </ListboxLabel>

    <div class="flex mt-1">
      <div class="relative flex items-stretch flex-grow focus-within:z-10">
        <span class="absolute inset-y-0 right-0 flex items-center pr-2" @click="search">
          <ChevronDown class="w-5 h-5 text-gray-400" aria-hidden="true" />
        </span>
        <LayoutPanelTop class="absolute left-2 w-5 h-5 top-2.5" />
        <input
          ref="searchInput"
          :value="selected_item ? selected_item.name : search_term"
          :disabled="disabled"
          autocomplete="off"
          :placeholder="translatedPlaceholder ? translatedPlaceholder : $t('Search...')"
          :class="[
            disabled ? 'text-common-grey' : '',
            inputClasses,
            isBoxBackground
              ? 'bg-light-box-background dark:bg-dark-box-background'
              : 'bg-light-screen-background dark:bg-dark-screen-background',
            'appearance-none block w-full px-3  py-2  border-gray-300 rounded-lg  placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light '
          ]"
          class="block w-full pl-8 transition ease-in-out rounded-lg focus:ring-blue focus:border-blue-light placeholder-common-grey border-light-divider-border dark:border-dark-divider-border duration-side-menu-transition-duration text-light-body-text dark:text-dark-body-text"
          @keydown.space.prevent="spaceClick"
          @blur="blur()"
          @click="search"
          @input="
            ($event) => {
              show_results = true
              search_term = $event.target.value
            }
          ">
      </div>
    </div>

    <transition
      v-show="show_results"
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0">
      <ListboxOptions
        static
        :class="[
          isBoxBackground
            ? 'bg-light-box-background dark:bg-dark-box-background'
            : 'bg-light-screen-background dark:bg-dark-screen-background',
          show_results ? 'ring-1 ring-black ring-opacity-5' : '',
          adjustHeight ? 'top-[49px]' : 'top-[49px]'
        ]"
        class="absolute shadow-lg inset-0 z-30 w-full bg-white max-h-60 rounded-md text-base overflow-auto focus:outline-none sm:text-sm"
        style="min-height: fit-content">
        <ListboxOption v-show="false" v-if="selected_item">
          <div class="flex justify-between">
            <div
              :class="''"
              class="relative py-2 pl-2 pr-4 transition ease-in-out rounded-lg group duration-side-menu-transition-duration" />
            <div
              class="relative cursor-pointer text-common-grey top-2 right-2 my-2 hover:text-dark-brand-blue transition-all ease-out duration-300"
              @click="reset">
              {{ $t("Clear") }}
            </div>
          </div>
        </ListboxOption>
        <ListboxOption v-if="search_results.length == 0 && search_term">
          <div class="flex items-center justify-center text-common-grey text-center pt-2 my-2">
            {{ $t("No template matching search query") }}
          </div>
        </ListboxOption>
        <ListboxOption v-else-if="search_results.length == 0">
          <div class="flex items-center justify-center text-common-grey text-center pt-2 my-2">
            {{ $t("No templates to display") }}
          </div>
        </ListboxOption>

        <ListboxOption v-if="selected_item" :value="selected_item">
          <div
            :class="''"
            class="relative py-2 pl-4 pr-4 transition ease-in-out rounded-lg cursor-pointer group/name hover:bg-light-brand-blue dark:hover:bg-dark-brand-blue duration-side-menu-transition-duration"
            @mouseenter="group_item_hover = selected_item.id"
            @mouseleave="group_item_hover = null">
            <span
              :class="['text-light-body-text dark:text-dark-body-text group-hover/name:text-dark-body-text']"
              class="flex font-bold truncate transition ease-in-out duration-side-menu-transition-duration">
              <Check class="mr-2 w-5 h-5 mt-0.5 font-extrabold" /> {{ selected_item.name }}
            </span>
            <Pencil
              :color="pencil_hover == selected_item.id ? '#2DA7D7' : 'white'"
              class="transition-all ease-out duration-300 absolute right-8 rounded-md hover:bg-white top-1 p-1 h-6 w-6 opacity-0 group-hover/name:opacity-100 hover:text-light-brand-blue"
              @click.stop="handleUpdateTemplate(selected_item)"
              @mouseenter="pencil_hover = selected_item.id"
              @mouseleave="pencil_hover = null" />
            <Trash2
              class="absolute transition-all ease-out duration-300 right-2 top-[5px] h-6 w-6 opacity-0 text-white group-hover/name:opacity-100 hover:bg-white hover:text-dark-brand-blue rounded-md p-1"
              @click.stop="handleDeleteTemplate(selected_item)" />
          </div>
        </ListboxOption>

        <ListboxOption v-for="item in search_results"
                       v-slot="{}"
                       :key="item"
                       :value="item"
                       @click="handleTemplateClick(item)">
          <div
            v-if="selected_item?.id != item.id"
            :class="''"
            class="relative py-2 pl-4 pr-4 transition ease-in-out rounded-lg cursor-pointer group/item hover:bg-light-brand-blue dark:hover:bg-dark-brand-blue duration-side-menu-transition-duration"
            @mouseenter="group_item_hover = item.id"
            @mouseleave="group_item_hover = null">
            <span
              :class="['text-light-body-text dark:text-dark-body-text group-hover/item:text-dark-body-text']"
              class="block truncate transition ease-in-out duration-side-menu-transition-duration">
              <CheckCheck
                class="inline mr-2 transition-all ease-out duration-300 w-4 h-4"
                :color="group_item_hover == item.id ? 'white' : '#899399'" />
              {{ item.name }}
              <Pencil
                :color="pencil_hover == item.id ? '#2DA7D7' : 'white'"
                class="transition-all ease-out duration-300 absolute right-8 rounded-md hover:bg-white top-1 p-1 h-6 w-6 opacity-0 group-hover/item:opacity-100 hover:text-light-brand-blue"
                @click.stop="handleUpdateTemplate(item)"
                @mouseenter="pencil_hover = item.id"
                @mouseleave="pencil_hover = null" />
              <Trash2
                class="absolute transition-all ease-out duration-300 right-2 top-[5px] h-6 w-6 opacity-0 text-white group-hover/item:opacity-100 hover:bg-white hover:text-dark-brand-blue rounded-md p-1"
                @click.stop="handleDeleteTemplate(item)" />
            </span>
          </div>
        </ListboxOption>
      </ListboxOptions>
    </transition>
  </Listbox>
</template>

<script>
import { Listbox, ListboxLabel, ListboxOption, ListboxOptions } from "@headlessui/vue"
import { CheckCheck, LayoutPanelTop, ChevronDown, Check, Trash2, Pencil } from "lucide-vue-next"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"

export default {
  components: {
    Listbox,
    ListboxLabel,
    ListboxOptions,
    ListboxOption,
    ChevronDown,
    Check,
    LayoutPanelTop,
    CheckCheck,
    Pencil,
    Trash2
  },
  props: {
    adjustHeight: {
      type: Boolean,
      default: false
    },
    items: {
      type: Array,
      required: true
    },

    label: {
      type: String,
      required: false
    },

    placeholder: {
      type: String,
      required: false,
      default: "Search for template"
    },
    inputClasses: {
      type: String,
      default: "text"
    },
    classes: {
      type: String,
      default: "text"
    },
    disabled: {
      type: Boolean,
      default: false
    },

    isBoxBackground: {
      type: Boolean,
      default: false
    },
    focusTrigger: {
      type: Boolean,
      default: false
    },
    resetTrigger: {
      type: Number,
      required: false
    },
    returnValue: {
      type: Boolean,
      default: true
    },
    currentItem: {
      type: [Object, String],
      required: false
    }
  },
  emits: ["update:modelValue", "templateSelection", "isEditTemplate"],
  data() {
    return {
      items_clone: null,
      show_results: false,
      search_term: "",
      search_results: [],
      selected_item: null,
      pencil_hover: null,
      group_item_hover: null
    }
  },
  computed: {
    translatedPlaceholder() {
      return this.$t(this.placeholder)
    }
  },
  watch: {
    currentItem() {
      this.selected_item = this.currentItem
    },
    resetTrigger() {
      this.reset()
    },
    search_term() {
      if (this.search_term != null && this.show_results) {
        this.search_results = this.items_clone.filter((item) => {
          return item.name.toLowerCase().includes(this.search_term.toLowerCase())
        })
      } else {
        this.search_results = this.items_clone
      }
    },

    focusTrigger() {
      setTimeout(() => {
        this.$refs.searchInput?.focus()
        this.$refs.searchInput?.click()
      }, 200)
    }
  },

  created() {
    this.items_clone = this.$utils.clone(this.items)
    if (this.currentItem) {
      this.selected_item = this.currentItem
    }
    this.register_listeners()
  },
  activated() {
    this.register_listeners()
  },
  unmounted() {
    this.unregister_listeners()
  },
  deactivated() {
    this.unregister_listeners()
  },

  methods: {
    register_listeners() {
      this.$emitter.on(ALL_EMITTERS.GROUP_UPDATED_IN_GROUP_DROPDOWN, (group) => {
        if (group.id == this.selected_item.id) {
          this.selected_item = group
        }
      })
      this.$emitter.on(ALL_EMITTERS.TEMPLATE_DELETED, (template_id) => {
        let index = this.items_clone.findIndex((item) => item.id == template_id)
        if (index != -1) {
          this.items_clone.splice(index, 1)
        }
      })
    },
    unregister_listeners() {
      this.$emitter.off(ALL_EMITTERS.GROUP_UPDATED_IN_GROUP_DROPDOWN)
      // this.$emitter.off(ALL_EMITTERS.TEMPLATE_DELETED)
    },
    handleDeleteTemplate(template) {
      this.$emitter.emit(ALL_EMITTERS.DELETE_TEMPLATE, { template: template })
    },
    handleUpdateTemplate(item) {
      this.selected_item = this.$utils.clone(item)
      this.$emit("templateSelection", this.selected_item.id)
      this.show_results = false
      this.$emit("update:modelValue", this.returnValue ? this.selected_item : this.selected_item.id)
      this.$emit("isEditTemplate", true)
    },
    spaceClick() {
      this.search_term = this.search_term + " "
    },
    handleTemplateClick(item) {
      this.selected_item = item
      this.$emit("templateSelection", this.selected_item.id)
      this.show_results = false
      this.$emit("update:modelValue", this.returnValue ? this.selected_item : this.selected_item.id)
      this.$emit("isEditTemplate", false)
    },
    search() {
      setTimeout(() => {
        this.search_results = this.items_clone
        this.$refs.searchInput.focus()
        this.show_results = true
      }, 200)
    },
    blur() {
      setTimeout(() => {
        this.show_results = false
      }, 50)
    },
    reset() {
      this.selected_item = null
      this.show_results = false
      this.search_term = ""
      this.$emit("update:modelValue", this.selected_item)
      this.$emit("isEditTemplate", false)
    }
  }
}
</script>

<style scoped>
.mh-48 {
  max-height: 10rem;
}
</style>
